/**
 * L1 — portfolio composite scoring configuration.
 */
export type MissingPillarMode = 'HARD_MISSING' | 'WORST_CASE_MISSING';

/**
 * Decision-scale score (1 good … 5 bad) above which a MEASURED pillar counts as
 * red. Lives here, not in `blockers.ts`, because the composite rationale and the
 * Path-to-GO blocker list must agree on what "red" means — they described the
 * same site differently while each owned its own threshold.
 */
export const RED_PILLAR_DECISION_THRESHOLD = 3.2;

/**
 * Benefit score (0 bad … 5 good) used for a pillar whose SOURCE REPORT EXISTS
 * but yielded no readable scoring signal.
 *
 * Neutral, deliberately — not `WORST_CASE_SCORE`. Two errors are possible here
 * and only one of them is acceptable:
 *
 *  - Dropping the pillar (the old behaviour) removes it from BOTH sides of the
 *    weighted mean, so deleting a below-mean pillar RAISES the composite. A
 *    maximally-opposed Public Sentiment report whose prose matches no regex
 *    made the social pillar VANISH instead of scoring badly — the live false-GO
 *    path.
 *  - Imputing WORST_CASE (0) would manufacture a red from a report we simply
 *    could not parse, producing false NO-GOs on sites that may be perfectly
 *    buildable.
 *
 * Neutral says what is actually true — "a report exists and we cannot read it"
 * — and, sitting well below the ~4.0 benefit a GO band requires, it blocks an
 * unearned GO without asserting a barrier that no fact supports.
 */
export const UNREADABLE_PILLAR_SCORE = 3;

export const PORTFOLIO_CONFIG = {
  /**
   * Part C SP4 (S4) — the single old `MISSING_PILLAR_MODE` controlled BOTH the
   * composite AGGREGATE and the per-pillar RENDER stamp at once, conflating two
   * decisions. It is now split:
   *
   *   MISSING_AGGREGATE_MODE — how a missing pillar affects the COMPOSITE SCORE.
   *     'HARD_MISSING' excludes missing pillars from the weighted denominator, so
   *     the composite reflects only the MEASURED pillars. This makes a missing
   *     pillar DISTINGUISHABLE from a measured-bad one (which used to both read
   *     as the worst score 0 and drag the aggregate down identically). The
   *     adequacy gate (`evaluateSiteDataAdequacy` / MIN_PILLAR_COVERAGE in
   *     deriveSiteVerdict) still WITHHOLDS a coloured verdict on sites whose
   *     coverage is too sparse to trust — so HARD_MISSING never lets a thin site
   *     assert a confident band; it only stops a partially-covered ADEQUATE site
   *     from being artificially depressed toward NO-GO by imputed worst fills.
   *
   *   MISSING_RENDER_MODE — how a missing pillar is STAMPED on the per-pillar
   *     breakdown for the deep dive. Kept 'WORST_CASE_MISSING' so a missing
   *     pillar still carries score 0 + the structured "No source reports" floor
   *     constraint (rendered as "Awaiting Data"), preserving the existing
   *     deep-dive UX. This is display-only and does NOT enter the aggregate.
   */
  MISSING_AGGREGATE_MODE: 'HARD_MISSING' as MissingPillarMode,
  MISSING_RENDER_MODE: 'WORST_CASE_MISSING' as MissingPillarMode,
  /**
   * @deprecated Part C SP4 (S4) — retained only as the RENDER-mode alias for
   * back-compat with external readers (metric explanations / test overrides).
   * New code must read MISSING_AGGREGATE_MODE or MISSING_RENDER_MODE explicitly.
   */
  MISSING_PILLAR_MODE: 'WORST_CASE_MISSING' as MissingPillarMode,
  WORST_CASE_SCORE: 0,
  SCORE_MIN: 0,
  SCORE_MAX: 5,
  MAX_DATA_COMPLETENESS_PENALTY: 0.30,
  DATA_CONFIDENCE_MISSING_REQUIRED_RATE: 0.10,
  DATA_CONFIDENCE_MISSING_REQUIRED_CAP: 0.20,
  DATA_CONFIDENCE_MISSING_RECOMMENDED_RATE: 0.05,
  DATA_CONFIDENCE_MISSING_RECOMMENDED_CAP: 0.10,
  DATA_CONFIDENCE_DANGLING_CITE_RATE: 0.02,
  DATA_CONFIDENCE_DANGLING_CITE_CAP: 0.10,
  DATA_CONFIDENCE_PROVENANCE_GAP_RATE: 0.05,
  DATA_CONFIDENCE_PROVENANCE_GAP_CAP: 0.15,
  DATA_CONFIDENCE_DISTRIBUTION_OUTLIER_RATE: 0.03,
  DATA_CONFIDENCE_DISTRIBUTION_OUTLIER_CAP: 0.10,
  DATA_CONFIDENCE_RECONCILIATION_BLOCK_RATE: 0.10,
  DATA_CONFIDENCE_RECONCILIATION_BLOCK_CAP: 0.20,
  // SED D1.1 — per-conflict data-confidence penalty for an UNRESOLVED
  // HIGH-severity evidence conflict (the strongest trust signal: a material
  // provider-vs-document disagreement the user has neither resolved nor had
  // auto-resolved). Additive on the risk/decision scale like the other
  // DATA_CONFIDENCE_* penalties. NOTE: the penalty alone (rate 0.10) lands a
  // single HIGH conflict in the 'R' tag, NOT 'C' — the load-bearing verdict
  // WITHHOLD is the adequacy C-trigger in deriveSiteVerdict, not this penalty.
  // FLAGGED FOR REVIEW: 0.10 is a product threshold (like MIN_PILLAR_COVERAGE).
  DATA_CONFIDENCE_UNRESOLVED_HIGH_CONFLICT_RATE: 0.10,
  DATA_CONFIDENCE_UNRESOLVED_HIGH_CONFLICT_CAP: 0.20,
  // ── Unmeasured decision-material pillar weight ────────────────────────────
  // `MISSING_AGGREGATE_MODE: 'HARD_MISSING'` excludes a missing pillar from the
  // weighted DENOMINATOR, which correctly stops an imputed worst-case fill from
  // dragging a partially-covered site toward NO-GO — but it also made absence
  // FREE. A site that never generated Public Sentiment simply had the social
  // pillar's 0.10 weight deleted, so its composite was computed over whatever
  // happened to exist and could still read GO. Missing diligence improved the
  // odds of a clean verdict, which is exactly the wrong incentive.
  //
  // The fix does NOT revert to WORST_CASE_MISSING (that is the opposite error:
  // scoring unmeasured as measured-bad). Unmeasured weight instead costs
  // CONFIDENCE — it feeds the same additive penalty ladder as every other
  // trust signal, so a thin site trends toward the 'C' tag and the adequacy
  // gate WITHHOLDS ("we don't know enough") rather than asserting a false
  // NO-GO.
  //
  // RATE applies per 1.0 of missing pillar weight (PILLARS weights sum to 1.0),
  // so the term is `min(rate × Σ missing weight, CAP)`. The calibration is
  // pinned by `__tests__/unmeasuredPillarPenalty.test.ts` — the boundary lands
  // exactly on the irreducible diligence floor:
  //   - feasibility ALONE                → 0.36 weight → 0.108          → 'R'
  //   - feasibility + power-availability → 0.36 weight → 0.108          → 'R'
  //
  // STAGE 5.2 — those two rows are now IDENTICAL, and the sentence that used to
  // stand here ("the two ALWAYS_REQUIRED_FOR_GO reports are precisely what
  // unlocks a coloured verdict on this axis") is no longer true in either half:
  // there is only ONE always-required report now (feasibility), and this axis
  // no longer distinguishes the two sets. Adding `feasibility` to the power
  // pillar's sources took the 0.21-weight pillar out of the unmeasured basis,
  // so feasibility-alone moved 0.57 → 0.36.
  //
  // CONSEQUENCE, stated plainly: this term no longer withholds anything a
  // feasibility-only site would have been withheld for. Whether such a site may
  // publish a coloured band is decided ENTIRELY by MIN_DECISION_WEIGHT_COVERAGE
  // in deriveSiteVerdict, which was RAISED to 0.65 on 2026-09-04 for exactly
  // this reason: at 0.60 that site landed on 0.54/0.90 = 0.60 — precisely ON
  // the threshold, and `< 0.60` is false — so one report published a band. It
  // now sits 0.05 BELOW the floor and is withheld. Reaching 'V' still requires
  // the supply-chain and sentiment diligence.
  // The CAP keeps this term from monopolising the 0.30 aggregate ceiling, so
  // the QA / conflict / provenance signals retain headroom to matter.
  DATA_CONFIDENCE_UNMEASURED_PILLAR_RATE: 0.30,
  DATA_CONFIDENCE_UNMEASURED_PILLAR_CAP: 0.18,
  DATA_CONFIDENCE_TAG_VERIFIED_MAX: 0.05,
  DATA_CONFIDENCE_TAG_REPORTED_MAX: 0.15,
  // ── SED D3.2 — value-level numeric uncertainty penalty (DEFAULT-OFF) ──
  // When `ENABLE_D3_UNCERTAINTY_SCORING` is FALSE (the default), the
  // uncertainty term is forced to 0 so the composite / confidenceTag /
  // decisionBand are byte-identical to today. Flip the flag ON only AFTER
  // the persisted `valueUncertainty` distributions are reviewed on real
  // sites (mirrors how D1.1's conflict input was staged). The penalty is a
  // capped count of WIDE bands (relativeWidthPct above the threshold).
  ENABLE_D3_UNCERTAINTY_SCORING: false,
  DATA_CONFIDENCE_WIDE_UNCERTAINTY_THRESHOLD_PCT: 40,
  DATA_CONFIDENCE_WIDE_UNCERTAINTY_RATE: 0.03,
  DATA_CONFIDENCE_WIDE_UNCERTAINTY_CAP: 0.10,
  // ── SED D3.3 — cross-source triangulation BONUS (DEFAULT-OFF) ──
  // When `ENABLE_D3_TRIANGULATION_SCORING` is FALSE (the default), the
  // bonus is forced to 0 (neutral) so no score moves. When ON, a high mean
  // agreement REDUCES the data-confidence penalty (a confidence lift),
  // capped, and never below 0 (the total penalty floor stays 0 — a bonus
  // can only offset existing penalties, never invent negative penalty).
  ENABLE_D3_TRIANGULATION_SCORING: false,
  DATA_CONFIDENCE_TRIANGULATION_BONUS_MAX: 0.05,
  DATA_CONFIDENCE_TRIANGULATION_MIN_AGREEMENT: 0.75,
  // ── R4a — structured finding-severity penalty ─────────────────────────────
  // Findings previously could not move the verdict at all. The three channels
  // that existed all failed to read severity as a MAGNITUDE:
  //   - `structuredFindings` were concatenated to prose and regex-scraped for
  //     NUMBERS, so severity was discarded outright;
  //   - `qaFindings` fed `confidenceFromReport`, which lowers a contribution's
  //     WEIGHT IN THE MEAN, never its score;
  //   - the data-confidence penalty could withhold via the 'C' tag.
  // So a `severity: 'critical'` RiskItem reading "no water rights, cannot be
  // permitted" moved the band by exactly zero.
  //
  // This term puts real risk on the SCORE axis, where it belongs. The
  // score/confidence split is deliberate and load-bearing: an EVIDENCE-QUALITY
  // problem should lower confidence (we are unsure), while a SITE problem
  // should lower the score (we are sure, and it is bad). Conflating them is
  // why findings were inert.
  //
  // Magnitudes are on the 0–5 BENEFIT scale and keep the 14:5:1 severity ratio
  // already established by `audit/trustScore.ts`. Only UNMITIGATED risks count
  // (an accepted risk with a stated mitigation is priced work, not exposure),
  // and only risks whose category maps to the pillar being scored.
  //
  // CAP = 1.0 = exactly one decision band on the benefit scale. Findings can
  // therefore lower a pillar by at most one band no matter how many are raised
  // — they inform the verdict, they never invert it, and no volume of
  // LLM-authored risk rows can manufacture a NO-GO.
  FINDING_SEVERITY_PENALTY_CRITICAL: 0.60,
  FINDING_SEVERITY_PENALTY_HIGH: 0.35,
  FINDING_SEVERITY_PENALTY_MEDIUM: 0.15,
  FINDING_SEVERITY_PENALTY_LOW: 0.05,
  MAX_FINDING_SEVERITY_PENALTY: 1.0,
  FOR_CALIBRATION: true,
  QUADRANT_RISK_THRESHOLD: 2.5,
  QUADRANT_OPPORTUNITY_THRESHOLD: 3.0,
  OPPORTUNITY_WEIGHT_CAPACITY: 0.30,
  OPPORTUNITY_WEIGHT_MARKET: 0.20,
  OPPORTUNITY_WEIGHT_TIMING: 0.20,
  OPPORTUNITY_WEIGHT_IX_UPSIDE: 0.15,
  OPPORTUNITY_WEIGHT_EXPANSION: 0.15,
} as const;
